import { useState } from "react";
import clsx from "clsx";
import { useSnapshot } from "valtio";
import { ENVIRONMENT } from "src/js/util/environment";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { openAddOnsStoreModal } from "src/js/stores/navigation/actions";
import { userStore } from "src/js/stores/user/store";
import { Intercom } from "src/js/util/intercom";
import { logEventV2 } from "react-migration/lib/util/logEventV2";
import { ModalV2 } from "react-migration/components/ModalV2/ModalV2";
import { TextSwitch } from "react-migration/components/TextSwitch";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { UpgradeOptionCard } from "./components/UpgradeOptionCard";
import { HOMETRACK_ADDON_ROUTE } from "../AddOnsStoreModal/constants";
import { getLicenseCode, getLicenseTier, LicenseTier } from "./getLicenseTier";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";

export const UpgradeModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { user } = useSnapshot(userStore);
  const isUSUser = hasFeature(Feature.usAccess);
  const hasAddonBeta = hasBetaFeature(Feature.addonUpgradeBeta);
  const [period, setPeriod] = useState<"monthly" | "yearly">("monthly");
  const license = getLicenseTier({ plan: user.plan_name });

  const onUpgrade = (selectedLicense: LicenseTier) => {
    logEventV2({
      name: "Upgrade",
      properties: {
        source: "SelfServiceUpgrade",
        action: license.code < getLicenseCode({ license: selectedLicense }) ? "upgrade" : "downgrade",
        currentLicense: license.name,
        selectedLicense,
      },
    }).finally(() => {
      window.location.href = `${ENVIRONMENT.ACCOUNTS_SERVICE_PRIVATE_API_URL}/customer-portal?flow=upgrade`;
    });
  };

  const onPremiumUpgrade = () => {
    logEventV2({
      name: "Upgrade",
      properties: {
        source: "ContactSupportUpgrade",
        action: "premium-upgrade",
        currentLicense: license.name,
        selectedLicense: LicenseTier.PREMIUM,
      },
    }).finally(() => {
      window.open(
        hasFeature(Feature.usAccess)
          ? "https://landtech.us/upgrade-to-premium?utm_campaign=14117383-US%3A%20PLG%20Website%20Launch&utm_source=in-app"
          : "https://land.tech/upgrade-to-unlimited",
        "_blank",
        "noopener"
      );
    });
  };

  const onContactSupport = (selectedLicense: LicenseTier) => {
    logEventV2({
      name: "Upgrade",
      properties: {
        source: "ContactSupportUpgrade",
        action: "contact-support",
        currentLicense: license.name,
        selectedLicense,
      },
    });
    Intercom("showNewMessage", `Upgrade to ${selectedLicense}`);
  };

  const handleHometrackUpgrade = () => {
    onClose();
    openAddOnsStoreModal({
      path: HOMETRACK_ADDON_ROUTE,
      logClickSource: "upgrade-modal",
    });
  };

  return (
    <ModalV2.Root defaultOpen={true} onOpenChange={onClose}>
      <ModalV2.Body
        titleSlot={
          <div className="flex justify-center flex-1 text-2xl font-semibold">{t("nav.upgrade_modal.title")}</div>
        }
        contentClassName={clsx(!isUSUser && license.name === LicenseTier.FREE ? "max-w-4xl!" : "max-w-3xl!")}
      >
        <section
          data-testid="upgrade-modal-description"
          className="flex flex-col items-center w-full gap-4 px-8 pb-4 text-base text-center md:text-lg"
        >
          {t("nav.upgrade_modal.description")}

          {!isUSUser && (
            <TextSwitch name="priceType" value={period} className="w-40">
              <TextSwitch.Label value="monthly" onToggle={setPeriod}>
                {t("nav.upgrade_modal.period.monthly")}
              </TextSwitch.Label>
              <TextSwitch.Label value="yearly" onToggle={setPeriod}>
                {t("nav.upgrade_modal.period.yearly")}
                <TextSwitch.Tag>-10%</TextSwitch.Tag>
              </TextSwitch.Label>
            </TextSwitch>
          )}
        </section>

        <section
          data-testid="upgrade-modal-options"
          className={clsx(
            "grid grid-cols-1 gap-4",
            !isUSUser && license.name === LicenseTier.FREE ? "lg:grid-cols-4" : "lg:grid-cols-3"
          )}
        >
          {/* Free tier option is always available for US users, but only shown when it's the active license for UK users */}
          {(isUSUser || (!isUSUser && license.name === LicenseTier.FREE)) && (
            <UpgradeOptionCard
              variant={LicenseTier.FREE}
              period={period}
              activeTier={license.code}
              onClick={() => onUpgrade(LicenseTier.FREE)}
            />
          )}
          {!isUSUser && (
            <UpgradeOptionCard
              variant={LicenseTier.STARTER}
              period={period}
              activeTier={license.code}
              onClick={
                user.self_upgrade_options.includes("starter")
                  ? () => onUpgrade(LicenseTier.STARTER)
                  : () => onContactSupport(LicenseTier.STARTER)
              }
            />
          )}
          <UpgradeOptionCard
            variant={LicenseTier.PRO}
            period={period}
            activeTier={license.code}
            onClick={
              user.self_upgrade_options.includes("pro")
                ? () => onUpgrade(LicenseTier.PRO)
                : () => onContactSupport(LicenseTier.PRO)
            }
          />
          <UpgradeOptionCard
            variant={LicenseTier.PREMIUM}
            period={period}
            activeTier={license.code}
            onClick={onPremiumUpgrade}
          />
        </section>
        {!isUSUser && hasAddonBeta && (
          <section data-testid="upgrade-modal-addons" className="pt-4">
            <div>{t("nav.upgrade_modal.addons.description")}</div>
            <div
              className={clsx(
                "grid grid-cols-1 gap-4 pt-4",
                license.name === LicenseTier.FREE ? "lg:grid-cols-4" : "lg:grid-cols-3"
              )}
            >
              <UpgradeOptionCard
                variant={LicenseTier.HOMETRACK}
                disabled={hasFeature(Feature.zooplaFullAccess)}
                period={period}
                activeTier={license.code}
                onClick={handleHometrackUpgrade}
              />
            </div>
          </section>
        )}
      </ModalV2.Body>
    </ModalV2.Root>
  );
};
UpgradeModal.displayName = "UpgradeModal";
